<template>
    <vx-expandable-card>
        <template v-slot:header> Custom Analytics </template>
        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <div class="flex items-center justify-between">
                    <div>
                        <vx-label :id="enabledLabelId" size="md">Enable Custom Analytics Link</vx-label>
                        <div :id="enabledDescriptionId" class="text-sm text-slate-500">
                            Allows linking to the custom analytics dashboard.
                        </div>
                    </div>
                    <vx-toggle
                        v-model="enabled"
                        size="lg"
                        :disabled="!isEditable"
                        :aria-labelledby="enabledLabelId"
                        :aria-describedby="enabledDescriptionId"
                    ></vx-toggle>
                </div>

                <div v-if="enabled">
                    <vx-label size="md">Dashboard GUID</vx-label>
                    <vx-input
                        v-model="team.guid"
                        size="lg"
                        :disabled="!isEditable"
                        :error="displayFirstError($externalResults.dashboard_url)"
                    ></vx-input>
                </div>

                <div v-if="enabled">
                    <vx-label size="md">URL Query String</vx-label>
                    <vx-input v-model="team.url_template" size="lg" :disabled="!isEditable"></vx-input>
                </div>

                <div class="help-block" v-if="enabled">
                    <vx-label size="md">Preview</vx-label>
                    <a
                        class="block font-mono font-normal break-all underline text-sky-700"
                        target="_blank"
                        :href="getDashboardUrl(team.guid, team.url_template, team.id)"
                    >
                        {{ getDashboardUrl(team.guid, team.url_template, team.id) }}
                    </a>
                </div>

                <vx-button v-if="isEditable" size="lg" @click.prevent="submit" :loading="submitting">
                    Update
                </vx-button>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxInput, VxToggle, VxButton, VxLabel } from '@voxie/frontend-components';
import { nextTick, watch, computed, onMounted, ref, useId } from 'vue';
import { adminTeamsClient } from '~/services';
import { displayFirstError } from '~/utils/validation';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';

defineProps({
    isEditable: {
        type: Boolean,
        default: true,
    },
});

const team = defineModel('team', {
    type: Object,
    required: true,
});

const toasted = useToasted();
const { $externalResults, catchError } = useFormRequestHandler();
const submitting = ref(false);
const enabled = ref(false);
const enabledLabelId = useId();
const enabledDescriptionId = useId();

onMounted(async () => {
    await nextTick();
    parseGuidAndTemplate();
    enabled.value = Boolean(team.value.dashboard_url && team.value.dashboard_url.length > 1);
    if (!team.value.dashboard_url) {
        team.value.dashboard_url = '';
    }
});

watch(enabled, () => {
    if (!enabled.value) {
        team.value.guid = '';
        team.value.url_template = '';
    }
});

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    $externalResults.value = {};

    try {
        await adminTeamsClient.updateCustomAnalytics(team.value.id, {
            dashboard_url: computedDashboardUrl.value,
        });

        team.value.dashboard_url = computedDashboardUrl.value;
        toasted.success('Custom analytics settings have been updated');
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const parseGuidAndTemplate = () => {
    if (typeof team.value.dashboard_url !== 'string' || team.value.dashboard_url.length === 0) {
        team.value.url_template = '';
        team.value.guid = '';
    } else {
        let parts1 = team.value.dashboard_url.split('?');
        team.value.url_template = parts1[1] || '';

        let parts2 = parts1[0].split('/');
        team.value.guid = parts2[parts2.length - 1];
    }

    return;
};

const computedDashboardUrl = computed(() => {
    if (team.value.guid === '' || !enabled.value) {
        return '';
    }

    return getDashboardUrl(team.value.guid, team.value.url_template);
});

const getDashboardUrl = (guid, template, teamId) => {
    let urlbase = 'https://metabase.voxie.com/public/dashboard/';

    let url = urlbase + guid;
    if (template) {
        url += '?' + template;
    }

    if (typeof teamId !== 'undefined') {
        return url.replace(/{team->id}/, teamId);
    }

    return url;
};
</script>
